import { subMonths } from 'date-fns';
import { z } from 'zod/v4';

import { OFFLINE_SALES_OPTMIZER_MODES } from './common';

import { getLatestSelectableDate } from '@/shared/utils/date';
import { PaginationSchema } from '@/shared/utils/schemas';

export const OfflineSalesOptimizerSearchParamsSchema = z.object({
  opt: z.enum(['diag', 'iex', 'how', 'plan']).catch('diag'), // optimizer tab
  diag: z.enum(['perf', 'detail']).catch('perf'), // diagnostics tab
  iex: z.enum(['vol', 'bdi']).catch('vol'), // insights explorer tab
  country: z.string().optional().catch(undefined),
  brand: z.string().optional().catch(undefined),
  mode: z
    .enum(Object.values(OFFLINE_SALES_OPTMIZER_MODES))
    .optional()
    .catch(OFFLINE_SALES_OPTMIZER_MODES.STANDARD),
  adGroups: z.array(z.string()).optional().catch(undefined),
  strategy: z
    .enum([
      'maximize_roi',
      'maximize_market_share_minimize_decay',
      'maximize_market_share',
      'velocity',
    ])
    .default('maximize_roi')
    .catch('maximize_roi'),
  fromDate: z.coerce
    .date()
    .default(() => subMonths(getLatestSelectableDate(), 1))
    .catch(() => subMonths(getLatestSelectableDate(), 1)),
  toDate: z.coerce
    .date()
    .default(() => getLatestSelectableDate())
    .catch(() => getLatestSelectableDate()),
  optStatus: z
    .enum(['optimized', 'not_optimized', 'all'])
    .default('all')
    .catch('all'),
  perfFlagged: z.boolean().default(true).catch(true),
  perfSearch: z.string().optional().catch(undefined),
  perfPage: PaginationSchema.shape.page,
  perfPageSize: PaginationSchema.shape.pageSize,
});

export type DetailedDiagnosticsOptimizedStatus = z.infer<
  typeof OfflineSalesOptimizerSearchParamsSchema
>['optStatus'];

export interface PerformanceMonitoringSearchParams {
  search?: z.infer<
    typeof OfflineSalesOptimizerSearchParamsSchema
  >['perfSearch'];
  flaggedOnly: z.infer<
    typeof OfflineSalesOptimizerSearchParamsSchema
  >['perfFlagged'];
  page: z.infer<typeof OfflineSalesOptimizerSearchParamsSchema>['perfPage'];
  pageSize: z.infer<
    typeof OfflineSalesOptimizerSearchParamsSchema
  >['perfPageSize'];
  mediaPlanId?: number;
}

export type OfflineSalesOptimizerStrategy = z.infer<
  typeof OfflineSalesOptimizerSearchParamsSchema
>['strategy'];

export type OfflineSalesOptimizerTabs = z.infer<
  typeof OfflineSalesOptimizerSearchParamsSchema
>['opt'];

export type DiagnosticsTabs = z.infer<
  typeof OfflineSalesOptimizerSearchParamsSchema
>['diag'];

export type InsightsExplorerTabs = z.infer<
  typeof OfflineSalesOptimizerSearchParamsSchema
>['iex'];
